/**
 * Reusable OTP code input component.
 *
 * Used by both login (EMAIL_OTP challenge, 8 digits) and registration (ConfirmSignUp, 6 digits).
 * Auto-advances focus between digit fields, fires `otp-submitted` event on completion.
 */

import { LitElement, html, css } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'

import '@material/web/button/text-button.js'

@customElement('auth-otp-verify')
export class AuthOtpVerify extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }

    .message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-lg, 24px);
      text-align: center;
      line-height: var(--cairn-line-height, 1.5);
    }

    .message strong {
      color: var(--cairn-text-color, #1e293b);
    }

    .digits {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .digit-input {
      width: 34px;
      height: 42px;
      border: 2px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      font-size: 18px;
      font-weight: 600;
      text-align: center;
      outline: none;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      transition: border-color 0.15s;
      -moz-appearance: textfield;
    }

    .digit-input::-webkit-outer-spin-button,
    .digit-input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    .digit-input:focus {
      border-color: var(--cairn-primary-color, #3b82f6);
    }

    .digit-input.error {
      border-color: var(--cairn-error-color, #dc2626);
    }

    .error-message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-error-color, #dc2626);
      margin: 0 0 var(--cairn-spacing-md, 16px);
      text-align: center;
    }

    .resend {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-xs, 4px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .countdown {
      font-variant-numeric: tabular-nums;
    }
  `

  @property() email = ''
  @property() error = ''
  @property({ type: Number }) length = 8

  @state() private digits: string[] = []
  @state() private resendCountdown = 0

  @query('.digit-input') private firstInput!: HTMLInputElement

  private resendTimer: ReturnType<typeof setInterval> | null = null

  override connectedCallback(): void {
    super.connectedCallback()
    this.digits = Array(this.length).fill('')
    this.startResendCooldown()
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    if (this.resendTimer) clearInterval(this.resendTimer)
  }

  override firstUpdated(): void {
    // Focus first input on mount
    this.firstInput?.focus()
  }

  private startResendCooldown(): void {
    this.resendCountdown = 60
    if (this.resendTimer) clearInterval(this.resendTimer)
    this.resendTimer = setInterval(() => {
      this.resendCountdown--
      if (this.resendCountdown <= 0 && this.resendTimer) {
        clearInterval(this.resendTimer)
        this.resendTimer = null
      }
    }, 1000)
  }

  private handleInput(index: number, e: Event): void {
    const input = e.target as HTMLInputElement
    const value = input.value.replace(/\D/g, '')

    // Handle paste of full code
    if (value.length > 1) {
      const chars = value.slice(0, this.length).split('')
      this.digits = [...chars, ...Array(this.length - chars.length).fill('')]
      this.requestUpdate()
      // Focus the last filled input or submit
      void this.updateComplete.then(() => {
        const inputs = this.shadowRoot!.querySelectorAll<HTMLInputElement>('.digit-input')
        const focusIndex = Math.min(chars.length, this.length - 1)
        inputs[focusIndex]?.focus()
        if (chars.length === this.length) this.submit()
      })
      return
    }

    this.digits = [...this.digits]
    this.digits[index] = value.slice(0, 1)
    this.requestUpdate()

    if (value && index < this.length - 1) {
      // Auto-advance to next input
      void this.updateComplete.then(() => {
        const inputs = this.shadowRoot!.querySelectorAll<HTMLInputElement>('.digit-input')
        inputs[index + 1]?.focus()
      })
    }

    // Auto-submit when all digits entered
    if (value && index === this.length - 1 && this.digits.every(d => d)) {
      this.submit()
    }
  }

  private handleKeydown(index: number, e: KeyboardEvent): void {
    if (e.key === 'Backspace' && !this.digits[index] && index > 0) {
      // Move back on backspace when current field is empty
      this.digits = [...this.digits]
      this.digits[index - 1] = ''
      this.requestUpdate()
      void this.updateComplete.then(() => {
        const inputs = this.shadowRoot!.querySelectorAll<HTMLInputElement>('.digit-input')
        inputs[index - 1]?.focus()
      })
    }
  }

  private submit(): void {
    const code = this.digits.join('')
    if (code.length === this.length) {
      this.dispatchEvent(
        new CustomEvent('otp-submitted', { detail: { code }, bubbles: true, composed: true })
      )
    }
  }

  private handleResend(): void {
    this.digits = Array(this.length).fill('')
    this.startResendCooldown()
    this.dispatchEvent(new CustomEvent('otp-resend', { bubbles: true, composed: true }))
    void this.updateComplete.then(() => this.firstInput?.focus())
  }

  override render(): unknown {
    const hasError = !!this.error

    return html`
      <p class="message">We sent a verification code to <strong>${this.email}</strong></p>

      <div class="digits">
        ${this.digits.map(
          (digit, i) => html`
            <input
              class="digit-input ${hasError ? 'error' : ''}"
              type="text"
              inputmode="numeric"
              maxlength="${this.length}"
              .value=${digit}
              @input=${(e: Event): void => this.handleInput(i, e)}
              @keydown=${(e: KeyboardEvent): void => this.handleKeydown(i, e)}
            />
          `
        )}
      </div>

      ${hasError ? html`<p class="error-message">${this.error}</p>` : ''}

      <div class="resend">
        ${this.resendCountdown > 0
          ? html`<span
              >Resend code in <span class="countdown">${this.resendCountdown}s</span></span
            >`
          : html`<md-text-button @click=${this.handleResend}>Resend code</md-text-button>`}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-otp-verify': AuthOtpVerify
  }
}
